import React, { useCallback, useEffect, useRef, startTransition, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, isUndefined, orderBy, toArray } from 'lodash-es';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { scroller } from 'react-scroll';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildModelsPath, buildStylesPath, VehicleModel } from 'client/data/models/vehicle-v2';
import { buildStyleBasicPathFromVinDecoder, VehicleVinModel } from 'client/data/models/vehicle-vin';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { PageModel } from 'client/data/models/page';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getQuery } from 'client/utils/location';
import {
  NEW_CORE_LEAD_SUBMITTER,
  PriceCheckerEntities,
  VEHICLE_SELECT_MODE_MAP,
} from 'site-modules/shared/constants/price-checker';
import { hasVehicleData, transformVinDecoder } from 'site-modules/shared/utils/price-checker';
import { PriceCheckerEmbeddedLeadForm } from 'site-modules/price-checker/components/price-checker-embedded-lead-form/price-checker-embedded-lead-form';
import { WhenDealerCalls } from 'site-modules/price-checker/components/when-dealer-calls/when-dealer-calls';
import { NeedMoreHelp } from 'site-modules/price-checker/components/need-more-help/need-more-help';
import { PriceCheckerCard } from 'site-modules/price-checker/components/price-checker-card/price-checker-card';
import { SmileySurvey } from 'site-modules/shared/components/smiley-survey/smiley-survey';
import { PRICE_CHECKER_SMILEY_SURVEY_CONFIG } from 'site-modules/price-checker/constants/price-checker';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { connect } from 'react-redux';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

const LEAD_FORM_OPTIONS = {
  headingClassName: 'heading-4',
  thankYouClassName: 'px-1 py-1_5',
  leadFormClassName: 'px-1 py-1_5 pt-md-0',
  thankYouHeaderComponent: null,
  thankYouLayoutOptions: {
    cardListColSize: { xs: 12, md: 7 },
    textColSize: { xs: 12, md: 5 },
    cardListClassName: null,
    showTruncatedView: true,
  },
};

export function PriceCheckerUI({
  make,
  model,
  year,
  styleId,
  vin,
  initialMake,
  initialModel,
  initialStyle,
  initialVinData = {
    vin: undefined,
  },
  initialPrices,
  sharedDeal = null,
  leadFormCreativeId = null,
  isMobile = false,
  hasChangeModel = true,
  isUtmLeadSubmitter = false,
  isIdmLeadSubmitter = false,
  visitorLocation,
  visitorId = null,
  isTestDeal = false,
  pageName = null,
  sessionId = null,
  setModelValue,
  isCore6830Enabled = false,
}) {
  const initialMode = get(sharedDeal, 'mode', vin ? VEHICLE_SELECT_MODE_MAP.VIN : VEHICLE_SELECT_MODE_MAP.MMY);
  const [vehicleSelectMode, setVehicleSelectMode] = useState(initialMode);
  const [leadFormParams, setLeadFormParams] = useState(
    initialStyle ? { vehicle: initialStyle, styleId: initialStyle?.id, trim: initialStyle?.trimName } : null
  );
  const [currentSharedDeal, setCurrentSharedDeal] = useState(sharedDeal);
  const resetMMYOnModeChange = useRef(initialMode === VEHICLE_SELECT_MODE_MAP.VIN);
  // For MMY mode
  const [vehicleParams, setVehicleParams] = useState({
    make: undefined,
    model: undefined,
    year: undefined,
    style: undefined,
  });
  // For vin mode
  const [vinParams, setVinParams] = useState({
    vin: undefined,
    makeName: undefined,
    makeSlug: undefined,
    modelName: undefined,
    modelSlug: undefined,
    styles: undefined,
    styleId: undefined,
    year: undefined,
  });

  const { zipCode } = visitorLocation;
  const isInternational = isUnknownZip(visitorLocation);

  useEffect(() => {
    const sharedDealZipCode = get(sharedDeal, 'zipCode', null);
    if (sharedDealZipCode && sharedDealZipCode !== zipCode) {
      setModelValue('location', VisitorModel, { zipCode: sharedDealZipCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateVehicleParams = useCallback(
    newVehicleParams => {
      setCurrentSharedDeal(null);
      setVehicleParams({ ...vehicleParams, ...newVehicleParams });
    },
    [vehicleParams]
  );

  const updateLeadFormParams = useCallback(params => {
    startTransition(() => {
      setLeadFormParams(params);
    });
  }, []);

  const updateVinParams = useCallback(
    newVinParams => {
      setVinParams({ ...vinParams, ...newVinParams });
    },
    [vinParams]
  );

  const resetSharedDealParams = useCallback(() => {
    setCurrentSharedDeal(null);
  }, []);

  const onModeChange = useCallback(
    event => {
      setCurrentSharedDeal(null);
      setVehicleSelectMode(get(event, 'target.dataset.value'));

      // this prevents form fields from being disabled
      // when first switching from VIN to MMY mode
      if (resetMMYOnModeChange.current) {
        resetMMYOnModeChange.current = false;
        startTransition(() => {
          updateVehicleParams({ make: null, model: null, year: null, style: null });
        });
      }
      if ([...get(event, 'target.classList', [])].includes('vin-btn')) {
        scroller.scrollTo('price-checker-card');
      }
    },
    [updateVehicleParams]
  );

  const currentVehicleParams = useMemo(
    () => ({
      make: make && isUndefined(vehicleParams.make) ? initialMake : vehicleParams.make,
      model: make && model && isUndefined(vehicleParams.model) ? initialModel : vehicleParams.model,
      year: make && model && year && isUndefined(vehicleParams.year) ? { year } : vehicleParams.year,
      style: make && model && year && styleId && isUndefined(vehicleParams.style) ? initialStyle : vehicleParams.style,
    }),
    [
      initialMake,
      initialModel,
      initialStyle,
      make,
      model,
      styleId,
      vehicleParams.make,
      vehicleParams.model,
      vehicleParams.style,
      vehicleParams.year,
      year,
    ]
  );
  const currentVinParams = vin && isUndefined(vinParams.vin) ? initialVinData : vinParams;

  const hasEmbeddedLeadForm = !isIdmLeadSubmitter && !isUtmLeadSubmitter;
  const areVehicleParamsReady = hasVehicleData({
    make,
    model,
    year,
    styleId,
    vin,
    vehicleParams: currentVehicleParams,
    vinParams: currentVinParams,
    vehicleSelectMode,
  });

  return (
    <Row>
      <Col xs={12} lg={10} xl={12}>
        <Row>
          <Col xs={12} xl={8}>
            <PriceCheckerCard
              vehicleSelectMode={vehicleSelectMode}
              hasChangeModel={hasChangeModel}
              areVehicleParamsReady={areVehicleParamsReady}
              onModeChange={onModeChange}
              updateVinParams={updateVinParams}
              updateVehicleParams={updateVehicleParams}
              updateLeadFormParams={updateLeadFormParams}
              vehicleParams={currentVehicleParams}
              vinParams={currentVinParams}
              initialPrices={initialPrices}
              leadFormCreativeId={leadFormCreativeId}
              hasEmbeddedLeadForm={hasEmbeddedLeadForm}
              isMobile={isMobile}
              zipCode={zipCode}
              sharedDeal={currentSharedDeal}
              resetSharedDealParams={resetSharedDealParams}
              visitorId={visitorId}
              showCalculators
              isTestDeal={isTestDeal}
              pageName={pageName}
              sessionId={sessionId}
              isCore6830International={isCore6830Enabled && isInternational}
            />
            {hasEmbeddedLeadForm && (
              <PriceCheckerEmbeddedLeadForm
                cardClassName="mb-2"
                forSaleClassName="mb-1_5"
                leadFormParams={leadFormParams}
                leadFormOptions={LEAD_FORM_OPTIONS}
                leadFormMinHeightType="PRICE_CHECKER_LEAD_FORM"
                isMobile={isMobile}
                isCore6830Enabled={isCore6830Enabled}
              />
            )}
          </Col>
          <Col xs={12} xl={4} className="mb-2 mb-xl-0 ps-xl-1 pt-xl-3_5">
            <WhenDealerCalls onModeChange={onModeChange} />
            {!isMobile && <SmileySurvey className="mt-2" {...PRICE_CHECKER_SMILEY_SURVEY_CONFIG} />}
          </Col>

          <Col xs={12} xl={8}>
            <NeedMoreHelp vehicleParams={currentVehicleParams} linksClassName="medium" />
            {isMobile && <SmileySurvey className="mt-2" {...PRICE_CHECKER_SMILEY_SURVEY_CONFIG} />}
          </Col>
        </Row>
      </Col>
      {isInternational && (
        <Experiment name="core-6830-pc-nozip" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal" />
        </Experiment>
      )}
    </Row>
  );
}

PriceCheckerUI.propTypes = {
  make: PropTypes.string,
  model: PropTypes.string,
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  styleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  vin: PropTypes.string,
  initialMake: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  initialModel: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  initialStyle: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  initialVinData: PropTypes.shape({
    vin: PropTypes.string,
    makeName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelName: PropTypes.string,
    modelSlug: PropTypes.string,
    styles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        trim: PropTypes.string,
        submodelName: PropTypes.string,
        slug: PropTypes.string,
      })
    ),
    styleId: PropTypes.number,
    year: PropTypes.number,
  }),
  initialPrices: PropTypes.shape({
    userDealerPrice: PropTypes.number,
    userMsrp: PropTypes.number,
  }),
  leadFormCreativeId: PropTypes.string,
  isMobile: PropTypes.bool,
  hasChangeModel: PropTypes.bool,
  isUtmLeadSubmitter: PropTypes.bool,
  isIdmLeadSubmitter: PropTypes.bool,
  visitorLocation: VisitorEntities.Location,
  sharedDeal: PriceCheckerEntities.SharedDataEntity,
  setModelValue: PropTypes.func.isRequired,
  visitorId: PropTypes.string,
  isTestDeal: PropTypes.bool,
  pageName: PropTypes.string,
  sessionId: PropTypes.string,
  isCore6830Enabled: PropTypes.bool,
};

const mapStateToProps = state => ({
  isAuthenticated: get(state, 'profile.isAuthenticated'),
  isCore6830Enabled:
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: 'core-6830-pc-nozip',
      defaultVal: 'ctrl',
    }) === 'chal',
});

export const PriceChecker = connect(mapStateToProps)(
  connectToModel(PriceCheckerUI, {
    initialMake: bindToPath(
      ({ make }) => !!make && 'makesList',
      VehicleModel,
      (makes, { make }) => makes.find(({ slug }) => slug === make)
    ),
    initialModel: bindToPath(
      ({ make, model }) => !!(make && model) && buildModelsPath({ make }),
      VehicleModel,
      (models, { model }) => get(models[model], 'slug') && models[model]
    ),
    initialStyle: bindToPath(
      ({ make, model, year }) => !!(make && model && year) && buildStylesPath({ make, model, year }),
      VehicleModel,
      (styles, { styleId }) =>
        styles.find(({ id }) => id === parseInt(styleId, 10)) || orderBy(toArray(styles), 'name')[0]
    ),
    initialVinData: bindToPath(
      ({ vin }) => !!vin && buildStyleBasicPathFromVinDecoder({ vin }),
      VehicleVinModel,
      transformVinDecoder
    ),
    isUtmLeadSubmitter: bindToPath(
      'location',
      PageModel,
      location => get(getQuery(location), 'utm_content') === NEW_CORE_LEAD_SUBMITTER
    ),
    isIdmLeadSubmitter: bindToPath(
      ({ isAuthenticated }) => isAuthenticated && 'data.idm',
      UserProfileModel,
      profile => !!get(profile, 'leads.length')
    ),
    visitorLocation: bindToPath('location', VisitorModel),
  })
);
